import { z } from 'zod';
import { baseMessageSchema } from './_base';

export enum OutboundMessageType {
  OUTBOUND_LIMITS = 'OUTBOUND_LIMITS',
  OUTBOUND_LIMITS_REPLY = 'OUTBOUND_LIMITS_REPLY',
  OUTBOUND_SMS = 'OUTBOUND_SMS',
  OUTBOUND_SMS_REPLY = 'OUTBOUND_SMS_REPLY',
  OUTBOUND_EMAIL = 'OUTBOUND_EMAIL',
  OUTBOUND_EMAIL_REPLY = 'OUTBOUND_EMAIL_REPLY',
  OUTBOUND_ERROR = 'OUTBOUND_ERROR',
}

// Payload schemas without base message fields
const outboundLimitsPayloadSchema = z.object({});

const outboundLimitsReplyPayloadSchema = z.object({
  email: z.object({
    count: z.number(),
    limit: z.number(),
    limitExpiry: z.string(),
  }),
  sms: z.object({
    count: z.number(),
    limit: z.number(),
    limitExpiry: z.string(),
  }),
});

const outboundSmsPayloadSchema = z.object({
  phone: z.string(),
  inviteId: z.string(),
  message: z.string().optional(),
});

const outboundSmsReplyPayloadSchema = z.object({
  success: z.boolean(),
  reachedLimit: z.boolean(),
  count: z.number(),
  limit: z.number(),
  limitExpiry: z.string(),
  errorMessage: z.string().optional(),
});

const outboundEmailPayloadSchema = z.object({
  email: z.string(),
  inviteId: z.string(),
  message: z.string().optional(),
});

const outboundEmailReplyPayloadSchema = z.object({
  success: z.boolean(),
  reachedLimit: z.boolean(),
  count: z.number(),
  limit: z.number(),
  limitExpiry: z.string(),
  errorMessage: z.string().optional(),
});

const outboundErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// Full message schemas including base fields
const outboundLimitsSchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_LIMITS),
  })
  .merge(outboundLimitsPayloadSchema);

const outboundLimitsReplySchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_LIMITS_REPLY),
  })
  .merge(outboundLimitsReplyPayloadSchema);

const outboundSmsSchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_SMS),
  })
  .merge(outboundSmsPayloadSchema);

const outboundSmsReplySchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_SMS_REPLY),
  })
  .merge(outboundSmsReplyPayloadSchema);

const outboundEmailSchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_EMAIL),
  })
  .merge(outboundEmailPayloadSchema);

const outboundEmailReplySchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_EMAIL_REPLY),
  })
  .merge(outboundEmailReplyPayloadSchema);

const outboundErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(OutboundMessageType.OUTBOUND_ERROR),
  })
  .merge(outboundErrorPayloadSchema);

export const outboundMessageSchema = z.discriminatedUnion('type', [
  outboundLimitsSchema,
  outboundLimitsReplySchema,
  outboundSmsSchema,
  outboundSmsReplySchema,
  outboundEmailSchema,
  outboundEmailReplySchema,
  outboundErrorSchema,
]);

// Types for the payloads
export type OutboundLimitsPayload = z.infer<typeof outboundLimitsPayloadSchema>;
export type OutboundLimitsReplyPayload = z.infer<typeof outboundLimitsReplyPayloadSchema>;
export type OutboundSmsPayload = z.infer<typeof outboundSmsPayloadSchema>;
export type OutboundSmsReplyPayload = z.infer<typeof outboundSmsReplyPayloadSchema>;
export type OutboundEmailPayload = z.infer<typeof outboundEmailPayloadSchema>;
export type OutboundEmailReplyPayload = z.infer<typeof outboundEmailReplyPayloadSchema>;
export type OutboundErrorPayload = z.infer<typeof outboundErrorPayloadSchema>;

// Type for the full messages
export type OutboundMessage = z.infer<typeof outboundMessageSchema>;

type PayloadType<T extends OutboundMessageType> = T extends OutboundMessageType.OUTBOUND_LIMITS
  ? OutboundLimitsPayload
  : T extends OutboundMessageType.OUTBOUND_LIMITS_REPLY
    ? OutboundLimitsReplyPayload
    : T extends OutboundMessageType.OUTBOUND_SMS
      ? OutboundSmsPayload
      : T extends OutboundMessageType.OUTBOUND_SMS_REPLY
        ? OutboundSmsReplyPayload
        : T extends OutboundMessageType.OUTBOUND_EMAIL
          ? OutboundEmailPayload
          : T extends OutboundMessageType.OUTBOUND_EMAIL_REPLY
            ? OutboundEmailReplyPayload
            : T extends OutboundMessageType.OUTBOUND_ERROR
              ? OutboundErrorPayload
              : never;

export function generateOutboundMessage<T extends OutboundMessageType>(type: T, payload: PayloadType<T>): OutboundMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case OutboundMessageType.OUTBOUND_LIMITS:
      return outboundLimitsSchema.parse({
        ...base,
        ...payload,
      });
    case OutboundMessageType.OUTBOUND_LIMITS_REPLY:
      return outboundLimitsReplySchema.parse({
        ...base,
        ...payload,
      });
    case OutboundMessageType.OUTBOUND_SMS:
      return outboundSmsSchema.parse({
        ...base,
        ...payload,
      });
    case OutboundMessageType.OUTBOUND_SMS_REPLY:
      return outboundSmsReplySchema.parse({
        ...base,
        ...payload,
      });
    case OutboundMessageType.OUTBOUND_EMAIL:
      return outboundEmailSchema.parse({
        ...base,
        ...payload,
      });
    case OutboundMessageType.OUTBOUND_EMAIL_REPLY:
      return outboundEmailReplySchema.parse({
        ...base,
        ...payload,
      });
    case OutboundMessageType.OUTBOUND_ERROR:
      return outboundErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
